import { Popover } from "@/atoms"
import { ICONS_INSIGHTS } from "@/const/insights-constants"
import { useTranslation } from "@/hooks/useTranslation"
import { IssueTypeIcon, Language } from "@/icons"
import { IconTag } from "@/molecules/IconTag"
import { checkInsightValue, InsightsIcons } from "@/molecules/InsightsIcons"
import { KodemScore } from "@/molecules/KodemScoreCell/KodemScore"

import styles from "./QuickWinIssueRow.module.scss"

import type { QuickWinIssueRowProps } from "."

const INSIGHT_ITEMS = [
  ICONS_INSIGHTS.runtime,
  ICONS_INSIGHTS.internetFacing,
  ICONS_INSIGHTS.exploitability.IN_THE_WILD,
  ICONS_INSIGHTS.ingress
]

export const QuickWinIssueRow = ({
  issue,
  language,
  onClick
}: QuickWinIssueRowProps) => {
  const { t } = useTranslation()
  const {
    title,
    riskId,
    packageName,
    severity,
    kodemScore,
    runtime,
    internetFacing,
    ingress,
    exploitability
  } = issue

  const subtitleText = t("quickWins.issueRow.riskIdOn", { riskId })

  const handleKeyDown = onClick
    ? (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick()
        }
      }
    : undefined

  const insightValues: Record<string, boolean> = {
    runtime: runtime === true,
    internetFacing: internetFacing === true,
    "exploitability.IN_THE_WILD":
      !!exploitability && exploitability !== "UNDETERMINED",
    ingress: ingress === true
  }

  return (
    <div
      className={`${styles.container}${onClick ? ` ${styles.clickable}` : ""}`}
      onClick={onClick}
      {...(onClick && {
        role: "button",
        tabIndex: 0,
        onKeyDown: handleKeyDown
      })}
    >
      <div className={styles.main}>
        <IssueTypeIcon
          type="Vulnerable_Package"
          severity={severity}
          size="small"
        />
        <div className={styles.text}>
          <span className={styles.title}>{title}</span>
          <span className={styles.subtitle}>
            <span className={styles.subtitleGroup}>{subtitleText}</span>
            <span className={styles.subtitleGroup}>
              {language && (
                <Language
                  language={language as Language}
                  className={styles.languageIcon}
                  noTooltip
                />
              )}
              {packageName}
            </span>
          </span>
        </div>
      </div>
      <div className={styles.insights}>
        <Popover
          content={
            <div className={styles.insightsPopover}>
              {INSIGHT_ITEMS.map((item, index) => {
                const value = checkInsightValue(insightValues[item.key])

                if (!value) return null

                return (
                  <IconTag
                    key={index}
                    icon={<item.Icon />}
                    text={t(`insights.${item.key}`)}
                  />
                )
              })}
            </div>
          }
          title={t("general.insights")}
        >
          <div>
            <InsightsIcons
              noTooltip
              items={INSIGHT_ITEMS}
              values={insightValues}
              hide
            />
          </div>
        </Popover>
      </div>
      <div className={styles.score}>
        <KodemScore score={kodemScore} />
      </div>
    </div>
  )
}
